import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { DirectionProvider } from '@radix-ui/react-direction';
import { useTranslation } from 'react-i18next';
import i18n, {
  availableLanguages,
  directionOf,
  LanguageMeta,
  loadLanguage,
  resolveInitialLanguage,
  saveLanguage,
  TextDirection,
} from './index';

interface LocaleContextValue {
  language: string;
  dir: TextDirection;
  languages: LanguageMeta[];
  /** Loads the pack if needed, then switches. Resolves false if the pack could not be loaded. */
  setLanguage: (code: string) => Promise<boolean>;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

/**
 * Owns the active language. It sets `lang` and `dir` on `<html>` (so browser
 * defaults, bidi and CSS logical properties follow the language), and wraps the
 * app in Radix's DirectionProvider so menus and selects open and navigate in the
 * right direction.
 */
export const LocaleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { i18n: instance } = useTranslation();
  const [language, setLanguageState] = useState(() => resolveInitialLanguage());
  // Bumped when a runtime pack finishes loading, so the direction is recomputed from its metadata.
  const [, setPackLoaded] = useState(0);

  // A saved language may be a runtime pack that is not registered yet. Load it on mount,
  // and fall back to English if it has gone.
  useEffect(() => {
    let cancelled = false;
    loadLanguage(language)
      .catch(() => {
        if (!cancelled) setLanguageState('en');
      })
      .finally(() => {
        if (!cancelled) setPackLoaded((n) => n + 1);
      });
    return () => {
      cancelled = true;
    };
    // Mount only: later switches go through setLanguage.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (instance.language !== language) {
      instance.changeLanguage(language);
    }
    document.documentElement.lang = language;
    document.documentElement.dir = directionOf(language);
  }, [instance, language]);

  const setLanguage = useCallback(async (code: string) => {
    try {
      await loadLanguage(code);
    } catch {
      return false;
    }
    await i18n.changeLanguage(code);
    saveLanguage(code);
    setLanguageState(code);
    return true;
  }, []);

  const dir = directionOf(language);

  return (
    <LocaleContext.Provider value={{ language, dir, languages: availableLanguages(), setLanguage }}>
      <DirectionProvider dir={dir}>{children}</DirectionProvider>
    </LocaleContext.Provider>
  );
};

export const useLocale = (): LocaleContextValue => {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used inside LocaleProvider');
  return ctx;
};
